import { mdiChevronDown, mdiTrayArrowDown } from '@mdi/js';
import Icon from '@mdi/react';
import { CircularProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import { observer } from 'mobx-react';
import React from 'react';
import { TooltipTransitionComponent } from '../../../base';
import { CoreCsvExportStore, csvExportStore as defaultCsvExportStore } from '../../../../stores/csv-export';
import { CsvBlobPartsBuilder, downloadCsvBlobParts } from './csv-utils';
import * as S from './table-export.styles';

export type ExportAllScope = 'open' | 'done' | 'all';

export interface ExportAllScopeOption {
    value: ExportAllScope;
    label: string;
}

export interface ExportAllFetchOptions<T = any> {
    /** Which messages to export; undefined when the button has no scope menu. */
    scope?: ExportAllScope;
    onProgress: (loaded: number, total: number) => void;
    signal?: AbortSignal;
    /**
     * Called in page order. Convert items to CSV immediately and do not retain
     * the message objects after this returns.
     */
    onPage: (items: T[], info: { page: number; total: number; loaded: number }) => void | Promise<void>;
}

export interface ExportAllButtonProps<T = any> {
    /** Expected total rows (for progress display). */
    total: number;
    /** Stream all pages via onPage; must not accumulate the full dataset. */
    onFetchAll: (options: ExportAllFetchOptions<T>) => Promise<void>;
    /** Map each item to a flat CSV row keyed by header (not retained as an array). */
    toCsvRow: (item: T) => Record<string, string | number | boolean | null | undefined>;
    /** Column headers in export order. */
    headers: string[];
    filename?: string;
    disabled?: boolean;
    /** Optional toast/notify callback for errors. */
    onError?: (message: string) => void;
    /** Defaults to the shared app-shell singleton. */
    csvExportStore?: CoreCsvExportStore;
    /** Title shown on the floating progress widget. */
    exportTitle?: string;
    /** When set, clicking the button opens a dropdown to choose the export scope. */
    scopes?: ExportAllScopeOption[];
}

const ExportAllButtonComponent = <T,>({
    total,
    scopes,
    onFetchAll,
    toCsvRow,
    headers,
    filename = 'export-all',
    disabled,
    onError,
    csvExportStore = defaultCsvExportStore,
    exportTitle = 'Exporting messages',
}: ExportAllButtonProps<T>): React.ReactElement => {
    const isExporting = csvExportStore.isActive;
    const mountedRef = React.useRef(true);

    React.useEffect(() => {
        mountedRef.current = true;
        // Intentionally do NOT abort on unmount — export runs on csvExportStore and
        // CsvExportWidget so users can navigate away while it continues.
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);

    const startExport = (scope?: ExportAllScope): void => {
        if (isExporting || disabled) {
            return;
        }

        const baseName = filename.replace(/\.csv$/, '');
        const csvFilename = scope && scope !== 'all' ? `${baseName}-${scope}.csv` : `${baseName}.csv`;

        void csvExportStore
            .start({
                // The tab counts reflect table filters and may not match a scoped export;
                // the real total arrives with the first page via onProgress.
                total: scope ? 0 : total,
                filename: csvFilename,
                title: exportTitle,
                execute: async ({ signal, onProgress }) => {
                    const csvBuilder = new CsvBlobPartsBuilder(headers);

                    await onFetchAll({
                        scope,
                        onProgress,
                        signal,
                        onPage: items => {
                            for (const item of items) {
                                csvBuilder.appendRow(toCsvRow(item));
                            }
                        },
                    });

                    if (signal.aborted) {
                        return;
                    }

                    const parts = csvBuilder.finish();
                    downloadCsvBlobParts(parts, csvFilename);
                },
            })
            .catch((err: any) => {
                // Avoid calling page-level onError after this button unmounted.
                if (!mountedRef.current) {
                    return;
                }
                if (csvExportStore.status === 'failed') {
                    onError?.(err?.message || 'Failed to export all messages');
                } else if (err?.message === 'An export is already in progress') {
                    onError?.(err.message);
                }
            });
    };

    const handleClick = (event: React.MouseEvent): void => {
        if (isExporting || disabled) {
            return;
        }
        if (scopes && scopes.length > 0) {
            setMenuAnchor(event.currentTarget as HTMLElement);
            return;
        }
        startExport();
    };

    const handleSelectScope = (option: ExportAllScopeOption): void => {
        setMenuAnchor(null);
        startExport(option.value);
    };

    return (
        <S.Container>
            <Tooltip
                placement="top-start"
                title={
                    isExporting
                        ? 'Export already in progress — see the progress panel'
                        : scopes
                          ? 'Export open, done, or all messages, ignoring table filters. This may take a while. You can navigate away while it runs.'
                          : 'Export ALL messages (inbox and done), ignoring table filters. This may take a while. You can navigate away while it runs.'
                }
                TransitionComponent={TooltipTransitionComponent}>
                <div>
                    <S.ActionButton
                        variant="outlined"
                        size="small"
                        type="button"
                        disabled={disabled || isExporting}
                        onClick={handleClick}
                        aria-haspopup={scopes ? 'menu' : undefined}
                        aria-expanded={scopes ? Boolean(menuAnchor) : undefined}
                        aria-label="Export all">
                        {isExporting ? (
                            <CircularProgress size={14} thickness={5} />
                        ) : (
                            <>
                                <Icon path={mdiTrayArrowDown} size={0.7} />
                                <S.ExportAllLabel>All</S.ExportAllLabel>
                                {scopes && <Icon path={mdiChevronDown} size={0.6} />}
                            </>
                        )}
                    </S.ActionButton>
                </div>
            </Tooltip>
            {scopes && (
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                    {scopes.map(option => (
                        <MenuItem key={option.value} dense onClick={() => handleSelectScope(option)}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </S.Container>
    );
};

// observer + generic component: cast to keep JSX generic usable
export const ExportAllButton = observer(ExportAllButtonComponent) as typeof ExportAllButtonComponent;
